import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { useSelector } from 'react-redux';
import { toast } from '../../../../CommonComponents/OPL/Toast/Toast';
import {
  dataToForm,
  FORM_SCHEMA,
  formToBody,
  PensionFormValues,
  UpsertPensionDTO,
} from './PensionForm.meta';
import {
  ADD_ASSET_SUCCESS_MESSAGE,
  CLONE_ASSET_SUCCESS_MESSAGE,
  EDIT_ASSET_SUCCESS_MESSAGE,
} from '../../OtherAssets.meta';
import { PensionDTO } from '../../tables/Pensions/Pensions.meta';
import { useUpsertPensionMutation } from '../../api/useUpsertPensionMutation';
import { OtherAssetsDTO } from '../../api/useOtherAssetsQuery';
import { onManagementSelect as baseOnManagementSelect } from '../components/ManagementStyle/ManagementStyle.meta';
import {
  EXCLUDE_OPT,
  getAssetBucketOptions,
} from '../components/AssetBucketSelect/AssetBucketSelect.meta';
import { selectCurrentClient } from '../../../../CommonSelectors/CommonAreaSelector';
import { GroupType, OptionType } from '../../../../CommonComponents/OPL/Select/BaseSelect/types';

interface Params {
  clientId: string;
  asset: PensionDTO | null;
  options: OtherAssetsDTO['Options'];
  onSuccess: () => void;
  readOnly?: boolean;
}

export const usePensionForm = ({ clientId, asset, options, onSuccess, readOnly }: Params) => {
  const formBag = useForm<PensionFormValues>({
    defaultValues: dataToForm({ data: asset, options }),
    resolver: yupResolver(FORM_SCHEMA),
    disabled: readOnly,
  });

  const { Dob: primaryDob, PartnerDob: spouseDob } = useSelector(selectCurrentClient);

  const primaryYob = primaryDob ? new Date(primaryDob).getFullYear() : null;
  const spouseYob = spouseDob ? new Date(spouseDob).getFullYear() : null;

  const mutationBag = useUpsertPensionMutation({ clientId });

  const ownerModel = formBag.watch('ownerModel');
  const isRecipientPrimary =
    ownerModel[0].Name === options.PensionOptions.recepientOptions.People[0].Name;

  const ageOptions = isRecipientPrimary
    ? options.PensionOptions.distributionAgeOptionsPrincipal
    : options.PensionOptions.distributionAgeOptionsSpouse;

  const distributionAgeOptions: OptionType<string>[] = ageOptions.map(distributionAge => {
    const ageNumber = Number(distributionAge);

    const yob = (isRecipientPrimary ? primaryYob : spouseYob) || 0;

    return {
      value: distributionAge.toString(),
      label: distributionAge.toString(),
      rightLabel: `Year ${ageNumber + yob}`,
    };
  });

  distributionAgeOptions.unshift({
    label: 'At Retirement',
    value: 'At Retirement',
    rightLabel: undefined,
  });

  const showSyncPlanValue = !!(
    asset?.IsIntegrated && !ADV.data.user.OptOuts.includes('IsIntegratedAdvancedOptions')
  );

  const onSubmit = formBag.handleSubmit(async (values: PensionFormValues) => {
    const body: UpsertPensionDTO = {
      clientId,
      Id: (!asset?.isClone && asset?.Id) || null,
      ...formToBody(values),
    };

    try {
      await mutationBag.mutateAsync(body);

      toast.success(
        asset?.Id
          ? asset.isClone
            ? CLONE_ASSET_SUCCESS_MESSAGE
            : EDIT_ASSET_SUCCESS_MESSAGE
          : ADD_ASSET_SUCCESS_MESSAGE,
      );

      onSuccess();
    } catch (error) {
      console.error(error);
    }
  });

  const [managementStyleSelection] = formBag.watch(['orionManagementStyleId']);

  const {
    AssetBucketingByManagementStyle: assetBucketingByManagementStyle,
    ManagementStyles: managementStyles,
    DisplayAssetBucketing: displayAssetBucketing,
  } = options;

  const baseAssetBucketOptions = [
    ...options.AvailableAssetBuckets.map(({ Title }, index) => ({
      label: Title,
      value: index,
      rightLabel: index === options.PensionOptions.DefaultAssetBucketIndex ? 'Default' : undefined,
    })),
    EXCLUDE_OPT,
  ];

  const managementStylesOptions = managementStyles.map(option => ({
    label: option.name,
    value: option.id,
    defaultBucketIndex: option.defaultBucket,
  }));

  const onManagementSelect = baseOnManagementSelect({
    options: managementStylesOptions,
    setValue: (value: number) => formBag.setValue('customBucketIndex', value),
  });

  const resetValues = () => {
    formBag.reset({ ...dataToForm({ data: asset, options }) });
  };
  const assetBucketOptions = getAssetBucketOptions({
    assetBucketOptions: baseAssetBucketOptions,
    assetBucketingByManagementStyle,
    managementStyleSelection,
    managementStylesOptions,
  });

  return {
    data: {
      formBag,
      mutationBag,
      distributionAgeOptions,
      showSyncPlanValue,
      managementStylesOptions,
      onManagementSelect,
      assetBucketOptions,
      assetBucketingByManagementStyle,
      managementStyleSelection,
      allowAssetBucketingCustomization: options.AllowAssetBucketingCustomization,
      displayAssetBucketing,
    },
    actions: {
      onSubmit,
      resetValues,
    },
  };
};
